import React, { ReactNode } from 'react';
import { PropGetters } from 'downshift';
import styled from '@emotion/styled';
import { ITheme } from '../../../themer';
import {
	DropdownItem,
	IDropdownItem,
	IGetItemDisplayValue,
} from '../../DropdownList';
import { Css } from '../../../utils';
import {
	SelectCurrent,
	SelectFloatingPlaceholder,
	SelectInput,
	SelectPlaceholder,
	SelectToggleIcon,
	StyledSelectMain,
} from '../SelectMain';

const SelectIcon = styled.div<{}, ITheme>`
	margin-right: ${Css.pxToRem(8)};
	display: flex;
	align-items: center;
	path {
		fill: ${(props) => props.theme.select.main.color};
	}
`;

const MultiselectCount = styled.span<{}, ITheme>`
	width: ${Css.pxToRem(24)};
	height: ${Css.pxToRem(24)};
	background-color: ${({ theme }) => theme.color.secondary};
	border-radius: 50%;
	color: ${({ theme }) => theme.color.onSecondary};
	display: flex;
	justify-content: center;
	align-items: center;
	font-size: ${Css.pxToRem(14)};
	line-height: normal;
`;

interface ISelectMainProps {
	getInputProps: PropGetters<IDropdownItem>['getInputProps'];
	getToggleButtonProps: PropGetters<IDropdownItem>['getToggleButtonProps'];
	getItemDisplayValue: IGetItemDisplayValue;
	selectedItem: IDropdownItem | null;
	placeholder?: string;
	ariaLabel?: string;
	disabled?: boolean;
	invalid?: boolean;
	isOpen: boolean;
	icon?: ReactNode;
	placeholderAsLabel?: boolean;
	multiSelectCount?: number;
}

export function MultiSelectMain({
	getInputProps,
	getToggleButtonProps,
	selectedItem,
	placeholder,
	ariaLabel,
	getItemDisplayValue,
	isOpen,
	disabled,
	invalid,
	icon,
	placeholderAsLabel,
	multiSelectCount,
}: ISelectMainProps) {
	const mainProps = disabled ? {} : getToggleButtonProps();

	return (
		<>
			<SelectInput
				{...getInputProps({ disabled })}
				aria-labelledby={undefined}
				aria-label={ariaLabel}
				aria-invalid={invalid}
			/>

			<StyledSelectMain
				{...mainProps}
				hasFloatingLabelStyle={selectedItem && placeholderAsLabel}
				disabled={disabled}
				invalid={invalid}
				className={isOpen ? 'is-open' : ''}
			>
				{icon && <SelectIcon>{icon}</SelectIcon>}
				<SelectCurrent>
					{placeholderAsLabel && (
						<SelectFloatingPlaceholder
							isOpen={isOpen}
							hasIcon={!!icon}
							hasValue={!!selectedItem}
						>
							{placeholder}
						</SelectFloatingPlaceholder>
					)}
					{selectedItem ? (
						<DropdownItem displayValue={getItemDisplayValue(selectedItem)} />
					) : (
						<SelectPlaceholder hasMultiselectCount={!!multiSelectCount}>
							{placeholder}
							{multiSelectCount ? (
								<MultiselectCount role="status" aria-live="polite">
									{multiSelectCount}
								</MultiselectCount>
							) : null}
						</SelectPlaceholder>
					)}
				</SelectCurrent>
				{!disabled && <SelectToggleIcon isOpen={isOpen} />}
			</StyledSelectMain>
		</>
	);
}
